import { useCallback, useEffect, useState } from "react";
import { DemoNote } from "@/components/DemoNote";
import { FitOutFixes } from "@/components/vision/FitOutFixes";
import { FixChecklist } from "@/components/FixChecklist";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Share2 } from "lucide-react";
import { api, assetUrl, errMsg } from "@/lib/api";
import { usePoll } from "@/lib/hooks";
import { BeforeAfter } from "@/components/BeforeAfter";
import { GroundingBadge, SiteFooter } from "@/components/Chrome";
import { DesignSpecCard } from "@/components/workspace/Design";
import { Controls, FailedVersion, Generating, GenerationRecord, otherEngine, VersionStrip } from "@/components/vision/Versions";
import { Walkthrough } from "@/components/vision/Walkthrough";
import { PublicProducts, SourcingPanel } from "@/components/vision/Sourcing";
import { Attribution } from "@/components/Provenance";
import { MakeItRealButton } from "@/components/MakeItReal";
import { ShopTheVision } from "@/components/vision/Shop";
import { VersionsCompare } from "@/components/vision/VersionsCompare";
import { shareLink } from "@/lib/share";
import { ProjectStatus } from "@/components/ProjectStatus";
import { OtherWays } from "@/components/OtherWays";

const statusText = (v) => (!v ? "—" : v.status === "succeeded" ? "Rendered" : v.status === "failed" ? "Failed" : "Rendering");

export default function VisionPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [vision, setVision] = useState(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/visions/${id}`).then((r) => setVision(r.data)).catch(() => setMissing(true)), [id]);
  useEffect(() => { load(); }, [load]);

  const latest = vision?.versions[vision.versions.length - 1];
  const pendingImage = latest && ["queued", "running"].includes(latest.status);
  const wt = vision?.walkthrough;
  const pendingVideo = wt && ["queued", "generating", "saving"].includes(wt.stage);
  usePoll(load, !!(pendingImage || pendingVideo), 2500);

  const act = async (fn) => {
    setBusy(true);
    try { const { data } = await fn(); setVision(data); } catch (e) { toast(errMsg(e, "Something went wrong. Try again.")); }
    setBusy(false);
  };
  const addVersion = (body) => act(() => api.post(`/visions/${id}/versions`, body));

  if (missing) {
    return (
      <main className="mx-auto max-w-3xl px-5 py-28" data-testid="vision-missing">
        <p className="font-display text-4xl tracking-tight">This vision isn't available.</p>
        <Link to="/" className="btn-primary mt-8" data-testid="vision-missing-home">Reimagine your own property</Link>
      </main>
    );
  }
  if (!vision) return <main className="mx-auto max-w-6xl px-5 py-16"><div className="aspect-[3/2] rounded bg-[var(--bone-2)] shimmer" /></main>;

  const { isOwner, current, original } = vision;
  const fromListing = original?.provenance?.provider === "bright_data";
  const NO_SHARE = fromListing ? "Confirm publishing rights to share this." : "This photo's license doesn't allow public sharing.";
  const noShare = original?.provenance?.allowedUses?.publicSharing === false;
  const confirmPublish = () => act(async () => { await api.post(`/properties/${vision.propertyId}/listing-photos/attest-publish`); toast("Publishing rights recorded"); return api.get(`/visions/${id}`); });
  const retryFailed = () => addVersion(latest.mode === "refine" ? { mode: "refine", instruction: latest.instruction, engine: latest.engine } : { mode: latest.mode === "engine" ? "engine" : "initial", engine: latest.engine });
  const otherFailed = () => addVersion(latest.mode === "refine" ? { mode: "refine", instruction: latest.instruction, engine: otherEngine(latest.engine) } : { mode: "engine", engine: otherEngine(latest.engine) });

  return (
    <main data-testid="vision-page" data-owner={isOwner}>
      <div className="mx-auto max-w-6xl px-5 pt-8 sm:px-8">
        {isOwner && <Link to={`/p/${vision.propertyId}`} className="btn-quiet mb-2" data-testid="back-to-workspace"><ArrowLeft size={15} /> {vision.propertyTitle}</Link>}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="eyebrow mb-2">Vision · {vision.roomName || "Room"}</div>
            <h1 className="font-display text-4xl leading-[1.05] tracking-tight sm:text-5xl" data-testid="vision-title">{vision.designSpec?.name || "A vision for this room"}</h1>
            <DemoNote show={vision.demoScenario} />
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <GroundingBadge status={vision.groundingStatus} />
              {vision.groundingNote && <span className="text-xs text-[var(--stone-dark)]" data-testid="grounding-note">{vision.groundingNote}</span>}
              <span className="font-mono-ui text-[0.68rem] uppercase tracking-[0.08em] text-[var(--stone-dark)]" data-testid="generation-status">
                Status: {statusText(current || latest)}{current ? ` · V${current.number} · ${current.engineLabel}` : ""}{current && latest && latest.id !== current.id && latest.status !== "succeeded" ? ` · latest attempt ${statusText(latest).toLowerCase()}` : ""}
              </span>
            </div>
          </div>
          {vision.project && <ProjectStatus project={vision.project} testid="vision-project-status" />}
          <div className="flex flex-wrap gap-2">
            <MakeItRealButton visionId={vision.id} disabled={!current || noShare} />
            <button className="btn-primary" onClick={() => (noShare ? toast(NO_SHARE) : shareLink(`${window.location.origin}/v/${vision.id}`, "A PlaceBacker vision"))} disabled={!current} data-testid="share-vision-button"><Share2 size={15} /> Share</button>
            {isOwner && (
              <button className="btn-ghost" disabled={!current || busy || (noShare && !vision.published)} onClick={() => act(() => api.post(`/visions/${id}/publish`, { published: !vision.published }))} data-testid={vision.published ? "unpublish-button" : "publish-button"}>
                {vision.published ? "Remove from Explore" : "Publish to Explore"}
              </button>
            )}
            {noShare && (
              <div className="w-full" data-testid="no-share-block">
                <p className="text-xs text-[#B42318]" data-testid="no-share-message">{NO_SHARE}</p>
                {fromListing && isOwner && (
                  <label className="mt-2 flex max-w-md items-start gap-2 text-xs" data-testid="publish-rights-confirm">
                    <input type="checkbox" className="mt-0.5" disabled={busy} onChange={(e) => e.target.checked && confirmPublish()} data-testid="publish-rights-checkbox" />
                    <span>I have the right to publish and share the resulting images and videos. <span className="text-[var(--stone-dark)]">Your confirmation is recorded. It doesn&apos;t independently prove ownership of these photos or override Zillow&apos;s terms.</span></span>
                  </label>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8">
          {current ? (
            <BeforeAfter before={assetUrl(original.url)} after={assetUrl(current.outputUrl)} width={original.width} height={original.height} />
          ) : (
            <div className="relative overflow-hidden rounded-xl" style={{ aspectRatio: `${original.width} / ${original.height}` }} data-testid="vision-original-only">
              <img src={assetUrl(original.url)} alt="Original" className="h-full w-full object-contain" />
              {pendingImage && <div className="absolute inset-0 shimmer" />}
            </div>
          )}
          <div className="mt-2 flex justify-between font-mono-ui text-[0.65rem] uppercase tracking-[0.08em] text-[var(--stone-dark)]">
            <span data-testid="original-dimensions">Original · {original.width}×{original.height}</span>
            {current && <span data-testid="current-dimensions">Vision · {current.width}×{current.height}</span>}
          </div>
          <div className="mt-1" data-testid="original-provenance">
            <span className="mr-1 text-[0.68rem] text-[var(--stone-dark)]">Original photo:</span>
            <Attribution provenance={original.provenance} testid="vision-photo-attribution" />
          </div>
          <FitOutFixes fixes={current?.fitOutProblems} />
          <OtherWays items={vision.inspirations} original={original} />
          {vision.roomId && <div className="mt-4"><FixChecklist roomId={vision.roomId} compact /></div>}
        </div>

        {isOwner && (
          <div className="mt-6 grid gap-4">
            {pendingImage && <Generating version={latest} />}
            {["failed", "rejected"].includes(latest?.status) && <FailedVersion version={latest} onRetry={retryFailed} onOther={otherFailed} busy={busy} />}
            {current && !pendingImage && (
              <Controls
                current={current}
                busy={busy}
                onRefine={(instruction) => addVersion({ mode: "refine", instruction })}
                onAlternative={() => addVersion({ mode: "alternative" })}
                onOther={() => addVersion({ mode: "engine", engine: otherEngine(current.engine) })}
              />
            )}
          </div>
        )}

        <section className="mt-12" data-testid="versions-section">
          <div className="eyebrow mb-4">Versions</div>
          <VersionStrip versions={vision.versions} currentId={vision.currentVersionId} isOwner={isOwner} onSelect={(v) => v.id !== vision.currentVersionId && act(() => api.put(`/visions/${id}/current`, { versionId: v.id }))} />
        </section>

        <div className="mt-16 grid gap-14 lg:grid-cols-[3fr_2fr]">
          <Walkthrough job={wt} done={vision.latestWalkthrough} isOwner={isOwner} canCreate={!!current && !pendingImage} creating={busy} onCreate={() => act(() => api.post(`/visions/${id}/walkthroughs`))} />
          <section data-testid="vision-design-spec">
            <div className="eyebrow mb-4">Design specification</div>
            <DesignSpecCard spec={vision.designSpec} testid="vision-spec" />
          </section>
        </div>

        {isOwner && <SourcingPanel visionId={id} canStart={!!current && !pendingImage} onGenerated={load} />}
        {!isOwner && <PublicProducts visionId={id} />}
        {current && <VersionsCompare visionId={id} width={original.width} height={original.height} refresh={vision.currentVersionId} />}
        {current && <ShopTheVision key={vision.currentVersionId} visionId={id} onChangeProducts={() => document.querySelector('[data-testid="sourcing-section"]')?.scrollIntoView({ behavior: "smooth" })} />}

        {isOwner && latest && <div className="mt-12"><GenerationRecord v={current || latest} /></div>}

        {!isOwner && (
          <section className="mt-20 border-t border-[var(--charcoal)]/15 pt-10" data-testid="public-cta-section">
            <p className="max-w-xl font-display text-3xl leading-snug tracking-tight">What place near you deserves a second life?</p>
            <button className="btn-primary mt-6" onClick={() => nav("/#import")} data-testid="reimagine-own-property-button">Reimagine your own property</button>
          </section>
        )}
      </div>
      <SiteFooter />
    </main>
  );
}
